// Drill: the lines of your repertoire, a line at a time. What you miss comes
// back sooner; what you know stops appearing.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Chess } from "chess.js";
import { errorText, getJson, isSignedOut, reportFault, sendJson, sleep } from "../app/api.ts";
import { persist, Superseded, type NetState } from "../app/net.ts";
import { refreshDrillBadge } from "../app/badge.ts";
import { scoreOf } from "../app/engine.ts";
import { cap, fmtDate, fmtScore, fmtTime, plyLabel } from "../app/format.ts";
import { TAG_MEANS, humanTag } from "../app/kinds.ts";
import { Icon } from "../app/icons.tsx";
import { prefs } from "../app/prefs.ts";
import { follow, goBack, navigate } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { PuzzleBoard, usePuzzle } from "../board/PuzzleBoard.tsx";
import type { MoveInfo } from "../board/puzzle.ts";
import type { Score } from "../board/scores.ts";
import { BoardMenu, BoardPlaceholder, BoardScreen } from "./BoardScreen.tsx";
import { LineMoves, LineRow } from "./LineRow.tsx";
import { OrList } from "./opening-text.tsx";

interface Accepted {
  uci: string;
  san: string;
  main?: boolean;
  comment?: string;
}

interface DrillStep {
  by: "you" | "opponent";
  epd: string;
  comment?: string;
  /** None for a move of a trap's combination the repertoire does not have. */
  card?: { new?: boolean };
  accepted?: Accepted[];
  /** The opponent's move is a trap. */
  trap?: boolean;
  /** A move of the combination that punishes it. */
  punish?: boolean;
}

/** The combination that punishes the trap a line runs into. */
interface Punish {
  /** Index in `steps` of its first move. */
  from: number;
  trap_san: string;
  sans: string[];
  motifs: string[];
  wins: { kind: "mate" } | { kind: "material"; cents: number } | { kind: "position" };
  white_eval: Score;
}

interface DrillLine {
  done?: false;
  steps: DrillStep[];
  start_fen: string;
  moves: string;
  name?: string;
  path?: { san: string }[];
  path_text?: string;
  punish?: Punish;
  due?: number;
  new_left?: number;
  repertoire?: { id: number; name: string; color: string };
}

interface DrillDone {
  done: true;
  next_due_at?: number;
}

export function DrillScreen({ params, query }: ScreenProps) {
  const repertoire = params.id || null;
  const from = query.get("from") || "";
  const [mode, setMode] = useState<"due" | "any">(query.get("mode") === "any" ? "any" : "due");
  const [line, setLine] = useState<{ kind: "loading" } | { kind: "error"; text: string } | { kind: "done"; data: DrillDone } | { kind: "line"; data: DrillLine; n: number }>({
    kind: "loading",
  });
  const session = useRef({ lines: 0, reviewed: 0, missed: 0, n: 0 });
  // The line being asked for, and how the connection is doing with it.
  const asking = useRef(0);
  const [net, setNet] = useState<NetState>({ kind: "ok" });
  const meta = {
    title: "Drill",
    nav: "openings",
    back: repertoire ? `/openings/${repertoire}` : "/openings",
    backLabel: repertoire ? "Repertoire" : "Openings",
  };

  const next = useCallback(
    async (asked = mode) => {
      setLine({ kind: "loading" });
      const q = new URLSearchParams({ mode: asked });
      if (repertoire) q.set("repertoire", repertoire);
      if (from) q.set("from", from);
      const mine = ++asking.current;
      try {
        const data = await persist((signal) => sendJson<DrillLine | DrillDone>("GET", "/api/drill?" + q, undefined, { signal }), {
          onState: (state) => mine === asking.current && setNet(state),
          superseded: () => mine !== asking.current,
        });
        setNet({ kind: "ok" });
        if (data.done) {
          refreshDrillBadge(true);
          setLine({ kind: "done", data });
        } else setLine({ kind: "line", data, n: ++session.current.n });
      } catch (err) {
        if (isSignedOut(err) || err instanceof Superseded) return;
        setNet({ kind: "ok" });
        setLine({ kind: "error", text: errorText(err) });
      }
    },
    [mode, repertoire, from]
  );

  useEffect(() => {
    void next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (line.kind === "line") {
    return (
      <div className="screen drill-screen">
        <BackButton meta={meta} />
        <DrillLineView key={line.n} data={line.data} mode={mode} session={session.current} onNext={() => void next()} back={meta.back} />
      </div>
    );
  }

  const didSome = session.current.reviewed > 0;
  return (
    <div className="screen drill-screen">
      <BackButton meta={meta} />
      <div className="screen-head">
        <div>
          <h1>Drill</h1>
          <p>A line at a time. What you miss comes back sooner; what you know stops appearing.</p>
        </div>
      </div>
      {line.kind === "loading" ? (
        <BoardPlaceholder
          status={net.kind === "offline" ? "No connection — trying again; the line comes as soon as it is back." : net.kind === "slow" ? "Connecting…" : "Choosing a line…"}
          lead={
            <button type="button" className="btn menu-btn" onClick={() => goBack(meta.back)}>
              <Icon name="back" />
              Back
            </button>
          }
          slots={
            <button type="button" className="btn advance" disabled>
              <Icon name="skip" />
              Skip line
            </button>
          }
        />
      ) : line.kind === "error" ? (
        <div className="notice error">{line.text}</div>
      ) : (
        <div className="card finished">
          <h2>{didSome ? "That's the session." : "Nothing due"}</h2>
          <p className="muted">
            {didSome
              ? `${session.current.reviewed} ${session.current.reviewed === 1 ? "position" : "positions"} reviewed in ${session.current.lines} ${session.current.lines === 1 ? "line" : "lines"}, ${session.current.missed} missed. Missed ones come back first.`
              : "Every position you have met is scheduled for later, and today's new ones are in."}
          </p>
          {line.data.next_due_at ? (
            <p className="faint">
              Next one due {fmtDate(line.data.next_due_at)} at {fmtTime(line.data.next_due_at)}.
            </p>
          ) : null}
          <div className="btn-row" style={{ justifyContent: "center", marginTop: "16px" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setMode("any");
                void next("any");
              }}
            >
              Drill anyway
            </button>
            <button type="button" className="btn" onClick={() => navigate(repertoire ? `/openings/${repertoire}` : "/openings")}>
              {repertoire ? "Repertoire" : "Openings"}
            </button>
            <button type="button" className="btn" onClick={() => navigate("/")}>
              Train
            </button>
          </div>
          {mode === "any" ? <p className="help">Drilling anyway plays lines without touching the schedule.</p> : null}
        </div>
      )}
    </div>
  );
}

function DrillLineView({
  data,
  mode,
  session,
  onNext,
  back,
}: {
  data: DrillLine;
  mode: "due" | "any";
  session: { lines: number; reviewed: number; missed: number };
  onNext: () => void;
  back: string;
}) {
  const steps = data.steps || [];
  // What is left, as of the last line the server chose, adjusted locally as
  // reviews go in so the counter moves while a line is being played.
  const [left, setLeft] = useState({ due: data.due || 0, fresh: data.new_left || 0 });
  const [note, setNote] = useState<ReactNode>(null);
  const [hint, setHint] = useState<{ index: number; level: "" | "square" | "arrow" }>({ index: -1, level: "" });
  const [done, setDone] = useState<{ moves: number; missed: number } | null>(null);
  const [building, setBuilding] = useState(false);
  const track = useRef({ reviewed: new Set<number>(), wrong: new Map<number, number>(), revealed: new Set<number>(), at: 0, yourMoves: 0, missedHere: 0, punishMissed: false });
  const punish = data.punish;

  // The labels count plies from the start of the game; the board counts
  // moves from where the line starts.
  const plyOffset = (() => {
    const parts = String(data.start_fen).split(/\s+/);
    const full = Number(parts[5]) || 1;
    return (full - 1) * 2 + (parts[1] === "b" ? 1 : 0);
  })();

  const nextYours = (i: number) => {
    for (let k = i; k < steps.length; k++) if (steps[k]?.by === "you") return k;
    return -1;
  };

  const prepared = (step: DrillStep) => {
    const accepted = step.accepted || [];
    return { accepted, main: accepted.find((a) => a.main) || accepted[0] };
  };

  // A trap is learned when the whole combination is found: its card, the
  // combination's first move, is answered once the line is over, and missed
  // if any move of it was. A move of it the repertoire does not have is
  // asked but has no card of its own.
  function review(index: number, result: "good" | "again") {
    const step = steps[index];
    if (punish && step?.punish && (index === punish.from || !step.card)) {
      if (result === "again") track.current.punishMissed = true;
      return;
    }
    commit(index, result);
  }

  function commit(index: number, result: "good" | "again") {
    const t = track.current;
    if (t.reviewed.has(index) || mode === "any") {
      t.reviewed.add(index);
      return;
    }
    t.reviewed.add(index);
    session.reviewed += 1;
    if (result === "again") {
      session.missed += 1;
      t.missedHere += 1;
    }
    const step = steps[index]!;
    // A new card stops being new; a missed one is due again at once; a due
    // card answered right is off the list until its next interval.
    setLeft((was) => {
      const now = { ...was };
      if (step.card?.new) now.fresh = Math.max(0, now.fresh - 1);
      else if (result === "good") now.due = Math.max(0, now.due - 1);
      if (result === "again" && step.card?.new) now.due += 1;
      return now;
    });
    // Sent until it is saved: a review lost to a dropped connection would
    // leave the card on the wrong day.
    persist((signal) => sendJson("POST", "/api/drill/review", { repertoire: data.repertoire?.id, epd: step.epd, result }, { signal })).catch((err) => {
      if (!isSignedOut(err)) toast(`Could not save that review: ${errorText(err)}`);
    });
  }

  /** A position never drilled is shown before it is asked: there is nothing
   *  to remember yet, and guessing is not learning. */
  function introduction(index: number): ReactNode {
    const step = steps[index];
    // A trap's punishment is a puzzle: it is found, not shown.
    if (!step || step.by !== "you" || !step.card?.new || step.punish) return null;
    const { accepted, main } = prepared(step);
    return (
      <>
        <div className="drill-note-title">
          <span className="badge neutral">New</span> Your line plays <OrList sans={accepted.map((a) => a.san)} /> here.
        </div>
        {main?.comment ? <p>{main.comment}</p> : null}
      </>
    );
  }

  const [puzzle, snapshot] = usePuzzle(
    { fen: data.start_fen, moves: data.moves },
    {
      onMove: (msg: MoveInfo) => onMove(msg),
      onSolved: () => {
        session.lines += 1;
        if (punish) {
          if (steps[punish.from]?.card) commit(punish.from, track.current.punishMissed ? "again" : "good");
          setNote(<Sprung punish={punish} ply={punish.from + plyOffset} missed={track.current.punishMissed} />);
        }
        setDone({ moves: track.current.yourMoves, missed: track.current.missedHere });
        refreshDrillBadge(true);
      },
      onFault: (f) => reportFault(f.kind, f.detail, { source: "board", fen: f.fen, moves: f.moves, board: `Drill: ${data.name || "your line"}` }),
      askScore: scoreOf,
    }
  );

  // Asking for the move is two presses: first the piece that moves, which
  // leaves where it goes to be found, then the move itself; a third takes the
  // hint away. Asking at all counts as not remembering. Shown after two wrong
  // tries, it is the move at once.
  function reveal(index: number, level?: "square" | "arrow") {
    const step = steps[index];
    if (!step || step.by !== "you") return;
    const { accepted, main } = prepared(step);
    const last = hint.index === index ? hint.level : "";
    const which = level || (last === "" ? "square" : last === "square" ? "arrow" : "clear");
    if (which === "clear") {
      setHint({ index, level: "" });
      puzzle.hint("clear");
      setNote(<span className="faint">Your move.</span>);
      return;
    }
    setHint({ index, level: which });
    track.current.revealed.add(index);
    review(index, "again");
    const moves = [main, ...accepted.filter((a) => a !== main)].filter((a): a is Accepted => Boolean(a?.uci)).map((a) => a.uci);
    puzzle.hint(which, moves);
    setNote(
      which === "square" ? (
        <div className="drill-note-title">The marked piece moves.</div>
      ) : (
        <>
          <div className="drill-note-title">
            {step.punish ? "The win goes on with" : "Your line:"} <OrList sans={step.punish ? [main!.san] : accepted.map((a) => a.san)} />
          </div>
          {main?.comment ? <p>{main.comment}</p> : null}
        </>
      )
    );
  }

  function onMove(msg: MoveInfo) {
    const t = track.current;
    const index = Number(msg.index);
    const step = steps[index];
    setHint({ index: -1, level: "" });
    if (msg.by === "opponent") {
      t.at = index + 1;
      if (step?.trap && punish) {
        setNote(<TrapSprung san={`${plyLabel(index + plyOffset)} ${msg.san}`} />);
        return;
      }
      const intro = introduction(t.at);
      setNote(
        <>
          {step?.comment ? (
            <>
              <div className="drill-note-title">
                {plyLabel(index + plyOffset)} {msg.san}
              </div>
              <p>{step.comment}</p>
            </>
          ) : intro ? null : (
            <span className="faint">{msg.san}. Your move.</span>
          )}
          {intro ? <div className={step?.comment ? "drill-note-next" : undefined}>{intro}</div> : null}
        </>
      );
      return;
    }
    if (!step || step.by !== "you") return;
    if (msg.correct) {
      t.at = index + 1;
      t.yourMoves += 1;
      review(index, (t.wrong.get(index) || 0) === 0 && !t.revealed.has(index) ? "good" : "again");
      const accepted = (step.accepted || []).find((a) => a.uci === msg.uci);
      if (step.punish) {
        setNote(
          <div className="drill-note-title good">
            <Icon name="check" /> {msg.san}. Keep going.
          </div>
        );
        return;
      }
      setNote(
        <>
          <div className="drill-note-title good">
            <Icon name="check" /> {msg.san}
          </div>
          {accepted?.comment ? <p>{accepted.comment}</p> : null}
        </>
      );
    } else {
      const count = (t.wrong.get(index) || 0) + 1;
      t.wrong.set(index, count);
      review(index, "again");
      if (count >= 2) setTimeout(() => reveal(index, "arrow"), 0);
      else if (step.punish) setNote(<span className="bad">Not {msg.san}. There is more here: look again.</span>);
      else setNote(<span className="bad">{msg.san} is not your line. Try again.</span>);
    }
  }

  useEffect(() => {
    // A line started where a trap has been played opens on the question.
    const last = data.path?.[data.path.length - 1];
    if (punish?.from === 0 && last) setNote(<TrapSprung san={`${plyLabel(plyOffset - 1)} ${last.san}`} />);
    else setNote(introduction(0) || <span className="faint">Your move.</span>);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Where the line ends is not where the game does. From there, play on
  // against the bot, or build the line on and walk it on the course's page.
  const played = (() => {
    const sans = [...(data.path || []).map((p) => p.san), ...String(data.moves || "").split(",").filter(Boolean)];
    const game = new Chess();
    const ucis: string[] = [];
    for (const san of sans) {
      try {
        const m = game.move(san);
        if (!m) break;
        ucis.push(m.from + m.to + (m.promotion || ""));
      } catch {
        break;
      }
    }
    return { sans: sans.slice(0, ucis.length), ucis, epd: game.fen().split(" ").slice(0, 4).join(" ") };
  })();
  const id = data.repertoire?.id;

  async function buildOn() {
    if (!id || building) return;
    setBuilding(true);
    try {
      const res = await sendJson("POST", `/api/repertoires/${id}/extend`, { epd: played.epd });
      let added = res.added || 0;
      if (!added && res.job_id) {
        toast("Nobody has played on from here. Asking the engine…");
        let job;
        for (;;) {
          job = await getJson(`/api/jobs/${res.job_id}`);
          if (job.status === "done" || job.status === "failed") break;
          await sleep(1200);
        }
        if (job.status === "failed") throw new Error(job.error || "The engine could not search that position.");
        added = job.result?.added || 0;
      }
      toast(added ? `${added} more ${added === 1 ? "position" : "positions"} of this line.` : "The book has nothing more to add here.");
      refreshDrillBadge(true);
      navigate(`/openings/${id}?line=${played.sans.map(encodeURIComponent).join("_")}`);
    } catch (err) {
      setBuilding(false);
      if (!isSignedOut(err)) toast(errorText(err));
    }
  }

  const hintLabel = hint.level === "square" ? "Show" : hint.level === "arrow" ? "Hide" : "Hint";
  const progress = [`${left.due} due`, `${left.fresh} new`, session.reviewed ? `${session.reviewed} reviewed` : "", mode === "any" ? "not scheduled" : ""]
    .filter(Boolean)
    .join(" · ");

  return (
    <BoardScreen
      className={"puzzle-screen drill-line" + (done ? " solved" : "")}
      board={<PuzzleBoard puzzle={puzzle} snapshot={snapshot} boardStyle={prefs.board} pieces={prefs.pieces} />}
      line={<LineRow puzzle={puzzle} snapshot={snapshot} />}
      bar={
        <>
          <button type="button" className="btn menu-btn" onClick={() => goBack(back)}>
            <Icon name="back" />
            Back
          </button>
          <button
            type="button"
            className={"btn hint-btn" + (done ? " spent" : "")}
            title="Which piece moves; press again for the move itself"
            onClick={() => reveal(hint.index >= 0 ? hint.index : nextYours(track.current.at))}
          >
            <Icon name="eye" />
            {hintLabel}
          </button>
          {done ? (
            <button type="button" className="btn btn-primary advance" onClick={onNext}>
              Next line
              <Icon name="next" />
            </button>
          ) : (
            <button type="button" className="btn advance" onClick={onNext}>
              <Icon name="skip" />
              Skip line
            </button>
          )}
          <BoardMenu
            shown={() => ({
              ...puzzle.shown(),
              headers: {
                Event: ["Blundermine", data.repertoire?.name, data.name].filter(Boolean).join(" — "),
                Site: location.href,
              },
            })}
          />
        </>
      }
      sheet={
        <>
          <div className="puzzle-badges">
            <span className="badge opening">{cap(data.repertoire?.color || "")}</span>
            {data.repertoire ? <a {...follow(`/openings/${data.repertoire.id}`)}>{data.repertoire.name}</a> : null}
          </div>
          <h2 className="puzzle-title">{data.name || data.repertoire?.name || "Your line"}</h2>
          {done ? (
            <div className="drill-done">
              <b>Line done</b> · {done.moves} {done.moves === 1 ? "move" : "moves"} · {done.missed} missed
              {id ? (
                <div className="drill-done-actions">
                  <button type="button" className="btn btn-sm" title="Play on from this position against the bot" onClick={() => navigate(`/openings/${id}/play?moves=${played.ucis.join(",")}`)}>
                    <Icon name="play" />
                    Play on
                  </button>
                  <button type="button" className="btn btn-sm" title="Build your line on from this position" disabled={building} onClick={() => void buildOn()}>
                    <Icon name="branch" />
                    {building ? "Building…" : "Build the line on"}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="drill-note">{note}</div>
          <LineMoves puzzle={puzzle} snapshot={snapshot} />
          {data.path_text ? <p className="puzzle-evals drill-path">{data.path_text}</p> : null}
          <div className="train-pos drill-progress">{progress}</div>
        </>
      }
    />
  );
}

/** They have played into the trap: the line becomes a puzzle. */
function TrapSprung({ san }: { san: string }) {
  return (
    <>
      <div className="drill-note-title">
        <span className="badge trap">Trap</span> They played {san}. It loses.
      </div>
      <p>Find the win.</p>
    </>
  );
}

/** What the combination came to, and what it is called. */
function Sprung({ punish, ply, missed }: { punish: Punish; ply: number; missed: boolean }) {
  const moves: string[] = [];
  punish.sans.forEach((san, i) => {
    const at = ply + i;
    moves.push(i === 0 || at % 2 === 0 ? `${plyLabel(at)} ${san}` : san);
  });
  const motifs = punish.motifs.filter((m) => m !== "mate");
  return (
    <>
      <div className={"drill-note-title" + (missed ? "" : " good")}>
        {missed ? null : <Icon name="check" />} {missed ? "That is the trap." : "You sprang the trap."}
      </div>
      <p className="drill-combination">
        {plyLabel(ply - 1)} {punish.trap_san}? {moves.join(" ")}
      </p>
      <p>{wins(punish)}</p>
      {motifs.length ? (
        <p className="drill-motifs">
          {motifs.slice(0, 2).map((m) => (
            <span key={m} className="tag" title={TAG_MEANS[m]}>
              {humanTag(m)}
            </span>
          ))}
          {TAG_MEANS[motifs[0]!] ? <span className="faint"> {TAG_MEANS[motifs[0]!]}</span> : null}
        </p>
      ) : null}
    </>
  );
}

/** What the combination wins, the number from White's side as always. */
export function wins(punish: Pick<Punish, "wins" | "white_eval">): string {
  const score = fmtScore(punish.white_eval);
  switch (punish.wins.kind) {
    case "mate":
      return "Mate.";
    case "material": {
      const c = punish.wins.cents;
      const what = c >= 900 ? "a queen's worth" : c >= 500 ? "a rook's worth" : c >= 300 ? "a piece's worth" : c >= 200 ? `${Math.round(c / 100)} pawns` : "a pawn";
      return `It wins ${what}, once everything that can be taken back is (${score}).`;
    }
    default:
      return `Nothing is taken yet, but the position is won (${score}).`;
  }
}
